import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Paper,
  alpha,
  Avatar,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { getFacilitiyByIdV2 } from '../../../../helpers/api';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import {
  Add,
  AdminPanelSettingsRounded,
  DeleteOutlineRounded,
  EditRounded,
  MoreHoriz,
  EmailOutlined,
  PhoneIphoneRounded,
  NotificationsActiveRounded,
  ShieldRounded,
} from '@mui/icons-material';
import PersonnelAdd from './PersonnelAdd';
import PersonnelDelete from './PersonnelDelete';
import PersonnelEdit from './PersonnelEdit';
import PersonnelInvite from './PersonnelInvite';
import { userCats } from '../../../../helpers/appstate';

export default function FacilityPersonnels({ facilityId, props }) {
  const { userCategory, userPermission } = props;

  const [personelList, setPersonelList] = useState([]);
  const [personelFetching, setPersonelLoading] = useState(false);
  const [personelSelected, setPersonelSelected] = useState();
  const [personelRefresh, setPersonelRefresh] = useState(false);
  const [showAddPersonel, setShowAddPersonel] = useState(false);
  const [showDeletePersonel, setShowDeletePersonel] = useState(false);
  const [showEditPersonel, setShowEditPersonel] = useState(false);
  const [showInvitePersonel, setShowInvitePersonel] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    handleFetchFacilityItem();
  }, [facilityId]);

  useEffect(() => {
    if (personelRefresh) handleFetchFacilityItem();
  }, [personelRefresh]);

  const handleMenuOpen = (event, record) => {
    setPersonelSelected(record);
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleFetchFacilityItem = async () => {
    setPersonelLoading(true);
    try {
      const res = await getFacilitiyByIdV2(facilityId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setPersonelList(data?.facilityPersonel || []);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Unable to fetch personnel list.' });
    } finally {
      setPersonelLoading(false);
      setPersonelRefresh(false);
    }
  };

  const compactCell = { style: { padding: '10px 14px' } };

  const renderContent = () => {
    const columns = [
      {
        name: 'fullName',
        label: 'Personnel Profile',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const p = personelList[index];
            return (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    width: 36,
                    height: 36,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                  }}
                >
                  {p?.fullName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    {p?.fullName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {p?.designation || 'Staff'}
                  </Typography>
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'email',
        label: 'Contact Information',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Box>
              <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}
              >
                <EmailOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />{' '}
                {personelList[index]?.email}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <PhoneIphoneRounded sx={{ fontSize: 12, color: 'text.disabled' }} />{' '}
                {personelList[index]?.phone || 'No phone'}
              </Typography>
            </Box>
          ),
        },
      },
      {
        name: 'isAdministrator',
        label: 'Role',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const isAdmin = personelList[index]?.isAdministrator;
            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isAdmin ? 'primary.main' : 'text.disabled',
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={isAdmin ? 700 : 500}
                  color={isAdmin ? 'primary.main' : 'text.secondary'}
                >
                  {isAdmin ? 'Admin' : 'Regular Staff'}
                </Typography>
              </Stack>
            );
          },
        },
      },
      {
        name: 'administratorStatus',
        label: 'Account Status',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const status = personelList[index]?.administratorStatus || 'ACTIVE';
            const isPending = status === 'PENDING';
            return (
              <Chip
                label={status}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  bgcolor: isPending ? alpha('#ed6c02', 0.1) : alpha('#2e7d32', 0.1),
                  color: isPending ? '#ed6c02' : '#2e7d32',
                  border: '1px solid',
                  borderColor: 'inherit',
                }}
              />
            );
          },
        },
      },
      {
        name: 'notification',
        label: 'Alerts',
        options: {
          setCellProps: () => compactCell,
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const p = personelList[index];
            return (
              <Stack direction="row" spacing={0.5}>
                {p?.receiveCertEmail === 'true' && (
                  <Tooltip title="Receives Certificates">
                    <Box
                      sx={{
                        bgcolor: alpha('#1976d2', 0.1),
                        color: 'primary.main',
                        p: 0.3,
                        borderRadius: '4px',
                        display: 'flex',
                      }}
                    >
                      <ShieldRounded sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                )}
                {p?.receiveInvoiceEmail === 'true' && (
                  <Tooltip title="Receives Invoices">
                    <Box
                      sx={{
                        bgcolor: alpha('#9c27b0', 0.1),
                        color: '#9c27b0',
                        p: 0.3,
                        borderRadius: '4px',
                        display: 'flex',
                      }}
                    >
                      <NotificationsActiveRounded sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            );
          },
        },
      },
    ];

    if (userCategory === userCats.EDM || userCategory === userCats.PERSONNEL) {
      columns.push({
        name: 'action',
        label: 'Action',
        options: {
          setCellProps: () => ({ style: { padding: '10px 14px', textAlign: 'center' } }),
          customHeadLabelRender: (a) => (
            <Typography variant="caption" fontWeight={700} color="textSecondary" align="center">
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, personelList[index])}>
              <MoreHoriz fontSize="small" />
            </IconButton>
          ),
        },
      });
    }

    return (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <MUIDataTable
          data={personelList}
          columns={columns}
          options={{
            elevation: 0,
            search: false,
            filter: false,
            download: false,
            print: false,
            viewColumns: false,
            selectableRows: 'none',
            responsive: 'standard',
            pagination: false,
            fixedHeader: true,
            tableBodyHeight: '65vh',
            setRowProps: (ev, dataIndex) => ({
              style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
            }),
            setTableProps: () => ({ size: 'small' }),
          }}
        />
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 2.5, alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
            Facility Personnel
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage internal staff roles and notification preferences
          </Typography>
        </Box>
        {(userCategory === userCats.EDM || userCategory === userCats.PERSONNEL) && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}
            onClick={() => setShowAddPersonel(true)}
          >
            Add Staff
          </Button>
        )}
      </Box>

      {personelFetching ? (
        <Box sx={{ p: 2 }}>
          <LoaderSkeleton />
          <Divider sx={{ my: 2 }} />
          <LoaderSkeleton />
        </Box>
      ) : personelList?.length < 1 ? (
        <Box
          sx={{ py: 8, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '12px' }}
        >
          <Empty description="No personnel assigned to this facility" />
        </Box>
      ) : (
        renderContent()
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            width: 220,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <MenuItem
          disabled={personelSelected?.isAdministrator}
          onClick={() => {
            handleMenuClose();
            setShowInvitePersonel(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <AdminPanelSettingsRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Promote to Admin"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setShowEditPersonel(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <EditRounded fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Update Profile"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setShowDeletePersonel(true);
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <DeleteOutlineRounded fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Remove Access"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'error.main' }}
          />
        </MenuItem>
      </Menu>

      {/* Dialogs remain identical in functionality */}
      {showAddPersonel && (
        <PersonnelAdd
          open={showAddPersonel}
          onClose={() => {
            setShowAddPersonel(false);
            setPersonelRefresh(true);
          }}
          facilityId={facilityId}
        />
      )}
      {showDeletePersonel && (
        <PersonnelDelete
          open={showDeletePersonel}
          onClose={() => {
            setShowDeletePersonel(false);
            setPersonelRefresh(true);
          }}
          facilityId={facilityId}
          personelInfo={personelSelected}
        />
      )}
      {showEditPersonel && (
        <PersonnelEdit
          open={showEditPersonel}
          onClose={() => {
            setShowEditPersonel(false);
            setPersonelRefresh(true);
          }}
          facilityId={facilityId}
          personelInfo={personelSelected}
        />
      )}
      {showInvitePersonel && (
        <PersonnelInvite
          open={showInvitePersonel}
          facilityId={facilityId}
          personelInfo={personelSelected}
          onClose={() => {
            setShowInvitePersonel(false);
            setPersonelRefresh(true);
          }}
        />
      )}
    </Box>
  );
}
